import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, { productGrid: Element, requiresPopup: boolean }>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('click', this.#handleAddButtonClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('click', this.#handleAddButtonClick, true);
    this.#abortController?.abort();
  }

  #productRequiresPopup(productGrid) {
    if (!productGrid) return false;

    const hasLineItemFields = Boolean(
      productGrid.querySelector('[data-line-item-input], .line-item-field, product-custom-property-component')
    );

    const variantPicker = productGrid.querySelector('variant-picker');
    const variantOptionCount = variantPicker
      ? variantPicker.querySelectorAll('input[type="radio"][data-option-value-id], select.variant-option__select').length
      : 0;
    const hasVariantChoices = variantOptionCount > 1;

    return hasLineItemFields || hasVariantChoices;
  }

  async #getProductConfig() {
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return null;

    const cached = this.#cachedContent.get(currentUrl);
    if (cached) return cached;

    const html = await this.fetchProductPage(currentUrl);
    if (!html) return null;

    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!(gridElement instanceof Element)) return null;

    const config = {
      productGrid: /** @type {Element} */ (gridElement.cloneNode(true)),
      requiresPopup: this.#productRequiresPopup(gridElement),
    };

    this.#cachedContent.set(currentUrl, config);
    return config;
  }

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #getSingleVariantAddButton(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('button.quick-add__button--add') : null
    );

    if (!button || !this.contains(button)) return null;
    if (this.dataset.hasMultipleVariants === 'true') return null;

    return button;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #submitQuickAddForm(form) {
    if (!form.checkValidity()) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  #dispatchCustomProductReinit() {
    document.dispatchEvent(new CustomEvent('custom-product:reinit'));
  }

  /**
   * Quick-add popup: render dropdown labels above selects instead of inline prefix.
   * @param {Element} root
   */
  #stackVariantSelectLabels(root) {
    const wrappers = root.querySelectorAll('.variant-option__select-wrapper--with-prefix');

    for (const wrapper of wrappers) {
      const prefix = wrapper.querySelector('.variant-option__select-prefix');
      const select = wrapper.querySelector('select.variant-option__select');
      const variantOption = wrapper.closest('.variant-option--dropdowns');

      if (!(prefix instanceof HTMLElement)) continue;
      if (!(select instanceof HTMLSelectElement)) continue;
      if (!(variantOption instanceof HTMLElement)) continue;

      const labelClass = 'quick-add-modal__select-label';
      const labelText = (prefix.textContent || '').trim();

      if (labelText && !variantOption.querySelector(`.${labelClass}`)) {
        const label = document.createElement('div');
        label.className = labelClass;
        label.textContent = labelText;
        variantOption.insertBefore(label, wrapper);
      }

      prefix.hidden = true;
      wrapper.classList.remove('variant-option__select-wrapper--with-prefix');
      select.style.paddingInlineStart = '';
      select.style.paddingLeft = '';
    }
  }

  #handleAddButtonClick = async (event) => {
    const button = this.#getSingleVariantAddButton(event);
    if (!(button instanceof HTMLButtonElement)) return;

    const form = button.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const config = await this.#getProductConfig();
    const shouldOpenPopup = Boolean(config?.requiresPopup);

    if (shouldOpenPopup) {
      await this.handleClick(event);
      return;
    }

    this.#submitQuickAddForm(form);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const config = await this.#getProductConfig();
    const productGrid = config?.productGrid ? /** @type {Element} */ (config.productGrid.cloneNode(true)) : null;

    if (productGrid) {
      await this.updateQuickAddModal(productGrid);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const preparedProductGrid = this.#prepareMobileModalContent(productGrid);
      if (!preparedProductGrid) return;
      productGrid = preparedProductGrid;
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#stackVariantSelectLabels(modalContent);
    this.#dispatchCustomProductReinit();
  }

  /**
   * @param {Element} productGrid
   * @returns {Element | null}
   */
  #prepareMobileModalContent(productGrid) {
    const productDetails = productGrid.querySelector('.product-details');
    if (!productDetails) return null;

    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const lineItemFields = Array.from(productDetails.querySelectorAll('.line-item-field, product-custom-property-component'));

    if (!productFormComponent || !productPrice) return null;

    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);
    productHeader.appendChild(productPrice);
    productGrid.appendChild(productHeader);

    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }

    for (const lineItemField of lineItemFields) {
      productGrid.appendChild(lineItemField);
    }

    productGrid.appendChild(productFormComponent);
    productDetails.remove();

    return productGrid;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
